import React, { useCallback, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { FormattedMessage, useIntl } from 'react-intl-next';
import Button from '@atlaskit/button/new';
import { Label } from '@atlaskit/form';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { layers } from '@atlaskit/theme/constants';
import UserPicker from '@atlaskit/user-picker';
import { AnyAri } from '@atlassian/ari';
import { FormSupportedPrincipalType } from '../../../../../common/types';
import { useFormPermissions } from '../../../../../controllers/form-permissions';
import { useSearchUsersAndGroups } from '../../../../../services/search-users-and-groups';
import { messages } from './messages';
export const PrincipalPicker = () => {
  const {
    formatMessage
  } = useIntl();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [{
    principals
  }, {
    addPrincipals
  }] = useFormPermissions();
  const {
    data,
    loading,
    error,
    searchUsersAndGroups
  } = useSearchUsersAndGroups();
  const options = (data || []).filter(searchedPrincipal => !principals.some(selectedPrincipal => convertOptionToPrincipal(searchedPrincipal).ari.equals(selectedPrincipal.ari)));
  const onQueryChange = useCallback(nextQuery => {
    if (nextQuery) {
      searchUsersAndGroups(nextQuery);
    }
  }, [searchUsersAndGroups]);
  const debouncedOnQueryChange = useMemo(() => debounce(onQueryChange, 500), [onQueryChange]);
  return /*#__PURE__*/React.createElement(Stack, null, /*#__PURE__*/React.createElement(Label, {
    htmlFor: "react-select-form-security-principal-picker-input"
  }, formatMessage(messages.userPickerInputLabel)), /*#__PURE__*/React.createElement(Inline, {
    space: "space.100"
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: pickerStyles
  }, /*#__PURE__*/React.createElement(UserPicker, {
    isMulti: true,
    appearance: "compact",
    width: "100%",
    ariaLabel: formatMessage(messages.userPickerInputLabel),
    fieldId: "form-security-principal-picker",
    options: options,
    onChange: newSelectedPrincipals => {
      if (Array.isArray(newSelectedPrincipals)) {
        setSelectedOptions(newSelectedPrincipals);
      }
    },
    onInputChange: debouncedOnQueryChange,
    isLoading: loading,
    value: selectedOptions,
    placeholder: "",
    addMoreMessage: "",
    noOptionsMessage: error ? formatMessage(messages.error) : null
    // These props are required for the user-picker dropdown to be "sent to the front"
    // of the element stack so that it appears in front of the modal footer.
    ,
    styles: {
      menuPortal: base => ({
        ...base,
        zIndex: layers.modal()
      })
    },
    menuPortalTarget: document.body
  })), /*#__PURE__*/React.createElement(Box, {
    xcss: addButtonStyles
  }, /*#__PURE__*/React.createElement(Button, {
    shouldFitContainer: true,
    isDisabled: selectedOptions.length === 0,
    onClick: () => {
      addPrincipals(selectedOptions.map(convertOptionToPrincipal));
      setSelectedOptions([]);
    }
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.add)))));
};
const convertOptionToPrincipal = ({
  id,
  type,
  name,
  avatarUrl
}) => ({
  ari: AnyAri.parse(id),
  type: type === 'group' ? FormSupportedPrincipalType.USER_GROUP : FormSupportedPrincipalType.USER,
  name,
  avatar: avatarUrl
});
const pickerStyles = xcss({
  width: '90%'
});
const addButtonStyles = xcss({
  width: '10%'
});